<template>
  <div
    class="sm:c-col-4 md:c-col-11 lg:c-col-10"
  >
    <div v-if="loading || performingLogout">
      <loading-page title="Validando acesso..." />
    </div>
    <div
      v-else
      class="c-row"
    >
      <div class="c-col-full md:c-col ui-min-w-0 md:c-d-flex md:ui-flex-col">
        <div
          v-if="!captchaToken"
          class="c-text-subtitle c-text-gray-400 ui-mb-6"
        >
          Confirme seu email de comunicação
        </div>

        <vue-hcaptcha
          v-if="!captchaToken"
          :sitekey="$env.VUE_APP_HCAPTCHA_SITE_KEY"
          @verify="response => setCaptchaToken(response)"
          @expired="() => clearCaptchaToken()"
          @error="() => clearCaptchaToken()"
        />

        <!-- Título e Mensagem -->
        <div
          v-if="captchaToken && emailValidationResult"
          class="ui-mb-4 md:ui-flex-grow"
        >
          <!-- Título baseado no resultado -->
          <div class="c-text-subtitle c-text-gray-400 ui-mb-4">
            <span v-if="emailValidationResult === 'success'">
              Email de comunicação confirmado com sucesso
            </span>
            <span v-else-if="emailValidationResult === 'already-validated'">
              Email já foi validado anteriormente
            </span>
            <span v-else-if="emailValidationResult === 'not-authorized'">
              Acesso Negado
            </span>
          </div>

          <!-- Mensagem baseada no resultado -->
          <div class="c-text-b2 c-text-gray-300">
            <span v-if="emailValidationResult === 'success'">
              Seu email de comunicação foi ativado. Você receberá notificações importantes neste endereço.
            </span>
            <span v-else-if="emailValidationResult === 'already-validated'">
              Este email de comunicação já estava ativo em sua conta. Você continuará recebendo notificações normalmente.
            </span>
            <span v-else-if="emailValidationResult === 'not-authorized'">
              Você não tem permissão para validar este email de comunicação. Este email pertence a outra conta.
            </span>
          </div>
        </div>

        <!-- Botão - sempre visível após validação -->
        <div
          v-if="captchaToken && emailValidationResult"
          class="ui-w-full ui-mt-4"
        >
          <router-link :to="{ name: 'meus-dados', query: getQuery() }">
            <c-button
              data-cy="go-to-profile-button"
              size="md"
              full
            >
              Ir para meus dados
            </c-button>
          </router-link>
        </div>
      </div>

      <!-- Ilustração -->
      <div
        id="image-wrapper"
        class="c-col-full md:c-col-4 ui-min-w-0 ui-mx-4 c-d-flex c-vertical-end c-horizontal-center md:c-horizontal-start"
      >
        <img
          class="ui-mt-4 ui--mb-8"
          src="@/assets/images/email-confirmed-illustration.svg"
          alt="Imagem de email confirmado"
        >
      </div>
    </div>
  </div>
</template>

<script>
import { CButton } from '@estrategiahq/coruja-web-ui';
import LoadingPage from '@/components/loading-page';
import VueHcaptcha from '@hcaptcha/vue-hcaptcha';
import { delayInSeconds } from '@/utils/async';
import { invalidUserIdAndTokenCombinationError, communicationEmailAlreadyActiveError, notAuthorizedError } from '@/utils/errors';

export default {
  components: {
    CButton,
    LoadingPage,
    VueHcaptcha,
  },
  data() {
    return {
      loading: false,
      performingLogout: false,
      captchaToken: '',
      emailValidationResult: null,
    };
  },
  async beforeMount() {
    // Se for a rota de logout, faz logout e redireciona para login
    if (this.$route.name === 'confirm-communication-email') {
      this.performingLogout = true;
      await this.performLogout();
      this.redirectToLogin();
    }
    // Se for a rota de processamento, verifica se o usuário está logado
    else if (this.$route.name === 'confirm-communication-email-process') {
      try {
        await this.$services.accounts.validate();
      } catch (error) {
        // Se não estiver logado, redireciona para login
        this.redirectToLogin();
        return;
      }
    }
  },
  methods: {
    /**
     * Realiza logout completo do usuário
     */
    async performLogout() {
      // Força o logoff do usuário de forma síncrona
      try {
        await this.$services.accounts.logout();
      } catch (error) {
        // Ignora erros de logout, pois o usuário pode não estar logado
      }

      // Limpa o token local preservando informações do parceiro
      const partner = localStorage.getItem('partner@estrategia') || null;
      localStorage.clear();
      if (partner) {
        localStorage.setItem('partner@estrategia', partner);
      }
    },

    /**
     * Redireciona para a página de login com parâmetros para retorno
     */
    redirectToLogin() {
      this.$router.push({
        name: 'login',
        query: {
          ...this.$route.query,
          returnTo: 'confirm-communication-email-process',
          returnToQuery: JSON.stringify(this.$route.query),
        },
      });
    },

    async validateCommunicationEmail () {
      this.loading = true;
      try {
        const query = this.$route.query;
        await Promise.all([
          delayInSeconds(2),
          this.$services.accounts.confirmCommunicationEmail(query.id, query.token, this.captchaToken),
        ]);

        this.emailValidationResult = 'success';
        this.$services.events.notifyCommunicationEmailConfirmationSuccess();
      } catch (e) {
        if (e === communicationEmailAlreadyActiveError) {
          this.emailValidationResult = 'already-validated';
        } else if (e === notAuthorizedError) {
          this.emailValidationResult = 'not-authorized';
        } else {
          this.goToProfile();

          if (e !== invalidUserIdAndTokenCombinationError) {
            throw e;
          }
        }
      } finally {
        this.loading = false;
      }
    },
    async setCaptchaToken (token) {
      this.captchaToken = token;
      await this.validateCommunicationEmail();
    },
    clearCaptchaToken () {
      this.captchaToken = '';
    },
    goToProfile() {
      this.$router.push({
        name: 'meus-dados',
        query: this.getQuery(),
      });
    },
    getQuery() {
      return {
        ...this.$route.query,
        id: undefined,
        token: undefined,
      };
    },
  },
};
</script>

<style scoped lang="scss">
/* medidas do figma */
#image-wrapper > img {
  width: 232px;
}

@include c-mq(md) {
  #image-wrapper > img {
    width: 275px;
    min-width: 275px; /* não deixa a coluna encolher a imagem */
  }
}

/* não deixa a coluna ficar maior que a imagem */
@include c-mq(md) {
  #image-wrapper {
    max-width: 275px;
  }
}
</style>
